import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { API, apiError, area, input, lbl, primaryBtn, setToken } from "./ambassadorApi";
import { ChannelsEditor } from "./ChannelsEditor";

const blank = { name: "", email: "", password: "", phone: "", country: "", city: "", paypal_email: "", about: "", audience: "",
  channels: [{ platform: "instagram", handle: "", followers: "" }] };

const Field = ({ id, label, children }) => (
  <div><label htmlFor={id} className={lbl}>{label}</label>{children}</div>
);

export const RegisterForm = ({ onDone }) => {
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (f.channels.some((c) => !c.handle.trim())) return toast.error("Add a handle or profile link for each platform.");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/ambassadors/register`, { ...f, channels: f.channels.map((c) => ({ ...c, followers: c.followers || null })) });
      setToken(r.data.token);
      toast.success("Application received — welcome to the Crew.");
      onDone?.(r.data.ambassador);
      navigate("/ambassadors/dashboard");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="ambassador-register-form" onSubmit={submit} className="space-y-8">
      <section className="rounded-3xl border border-[#e3e2dc] bg-[#faf9f6] p-6 sm:p-8 space-y-4">
        <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#ff2a85]">01 · About you</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field id="amb-name" label="Full name"><input id="amb-name" data-testid="amb-reg-name" className={input} value={f.name} onChange={set("name")} required minLength={2} /></Field>
          <Field id="amb-email" label="Email (your login)"><input id="amb-email" data-testid="amb-reg-email" type="email" className={input} value={f.email} onChange={set("email")} required /></Field>
          <Field id="amb-password" label="Password (8+ characters)"><input id="amb-password" data-testid="amb-reg-password" type="password" className={input} value={f.password} onChange={set("password")} required minLength={8} /></Field>
          <Field id="amb-phone" label="Phone / WhatsApp"><input id="amb-phone" data-testid="amb-reg-phone" className={input} value={f.phone} onChange={set("phone")} /></Field>
          <Field id="amb-country" label="Country"><input id="amb-country" data-testid="amb-reg-country" className={input} value={f.country} onChange={set("country")} required /></Field>
          <Field id="amb-city" label="City"><input id="amb-city" data-testid="amb-reg-city" className={input} value={f.city} onChange={set("city")} /></Field>
        </div>
      </section>

      <section className="rounded-3xl border border-[#e3e2dc] bg-[#faf9f6] p-6 sm:p-8 space-y-4">
        <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#ff2a85]">02 · Where you promote</p>
        <ChannelsEditor channels={f.channels} onChange={(channels) => setF({ ...f, channels })} />
        <Field id="amb-audience" label="Who follows you? (optional)"><input id="amb-audience" data-testid="amb-reg-audience" className={input} value={f.audience} onChange={set("audience")} placeholder="e.g. Muslim youth in the UK, 18–30" /></Field>
        <Field id="amb-about" label="How will you represent Deen District? (optional)"><textarea id="amb-about" data-testid="amb-reg-about" className={area} value={f.about} onChange={set("about")} placeholder="Content ideas, communities you're part of, past brand work…" /></Field>
      </section>

      <section className="rounded-3xl border border-[#e3e2dc] bg-[#faf9f6] p-6 sm:p-8 space-y-4">
        <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#ff2a85]">03 · Getting paid</p>
        <Field id="amb-paypal" label="PayPal email (commissions are paid here)"><input id="amb-paypal" data-testid="amb-reg-paypal" type="email" className={input} value={f.paypal_email} onChange={set("paypal_email")} required /></Field>
        <p className="text-xs text-[#5f5e5a] leading-relaxed">Commissions are calculated on the profit of every piece sold through your link and paid to this PayPal account once the order ships.</p>
      </section>

      <button type="submit" data-testid="amb-reg-submit" disabled={busy} className={primaryBtn}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : null} Apply to join the Crew
      </button>
    </form>
  );
};
